import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Activity, AlertCircle, CreditCard } from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { CancelConfirmDialog } from '@/components/subscription/CancelConfirmDialog'
import { LockedResourcesAlert } from '@/components/subscription/LockedResourcesAlert'
import { PlanCard } from '@/components/subscription/PlanCard'
import { PlanConfirmDialog } from '@/components/subscription/PlanConfirmDialog'
import { SubscriptionStats } from '@/components/subscription/SubscriptionStats'
import { UsageMeter } from '@/components/subscription/UsageMeter'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useCallingEnabled } from '@/hooks/useCalling'
import { usePermission } from '@/hooks/usePermission'
import {
  cancelSubscription,
  changePlan,
  getLockedResources,
  getSubscriptionPlans,
  getSubscriptionStatus,
  getSubscriptionUsage,
  subscribe,
  undoCancellation,
} from '@/services/api/subscription.service'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { formatDate } from '@/utils/date'
import { getErrorMessage } from '@/utils/error-parser'
import { formatLimitValue, formatUsageValue, numericLimit } from '@/utils/quota'

export function SubscriptionPage() {
  const queryClient = useQueryClient()
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const plansRef = useRef<HTMLDivElement>(null)

  const { can } = usePermission()
  const canView = can(PERMISSION_KEYS.SUBSCRIPTION_VIEW)
  const canCancel = can(PERMISSION_KEYS.SUBSCRIPTION_CANCEL)

  const { data: plans, isLoading: plansLoading } = useQuery({
    queryKey: ['subscription', 'plans'],
    queryFn: getSubscriptionPlans,
  })

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['subscription', 'status'],
    queryFn: getSubscriptionStatus,
    enabled: canView,
  })

  const { enabled: callingEnabled } = useCallingEnabled()
  const { data: usage, isLoading: usageLoading } = useQuery({
    queryKey: ['subscription', 'usage'],
    queryFn: getSubscriptionUsage,
    enabled: canView,
  })

  const { data: lockedResources } = useQuery({
    queryKey: ['subscription', 'locked-resources'],
    queryFn: getLockedResources,
    enabled: canView && !!status?.subscriptionId,
  })

  // Calling is hidden while it is switched off for the deployment.
  const usageItems = (usage?.usage ?? []).filter(
    (item) => callingEnabled || item.featureKey !== 'CALLING'
  )

  const subscribeMutation = useMutation({
    mutationFn: subscribe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      toast.success('Subscription activated ')
      setSelectedPlanCode(null)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to subscribe'))
    },
  })

  const changePlanMutation = useMutation({
    mutationFn: changePlan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      toast.success('Plan changed')
      setSelectedPlanCode(null)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to change plan'))
    },
  })

  const cancelMutation = useMutation({
    mutationFn: cancelSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      toast.success('Subscription scheduled for cancellation')
      setCancelDialogOpen(false)
    },
    onError: () => {
      toast.error('Failed to cancel subscription')
    },
  })

  const undoCancelMutation = useMutation({
    mutationFn: undoCancellation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      toast.success('Subscription cancellation reversed')
    },
    onError: () => {
      toast.error('Failed to undo cancellation')
    },
  })

  const handlePlanSelect = (planCode: string) => {
    setSelectedPlanCode(planCode)
  }

  const handleConfirmPlan = () => {
    if (!selectedPlanCode) return

    if (!status?.subscriptionId) {
      subscribeMutation.mutate({ templateCode: selectedPlanCode })
    } else {
      changePlanMutation.mutate({ templateCode: selectedPlanCode })
    }
  }

  const scrollToPlans = () => {
    plansRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const selectedPlan = plans?.find((p) => p.code === selectedPlanCode) ?? null
  const isLoading = plansLoading || statusLoading

  return (
    <div className="space-y-6">
      {/* Page Header matching Record Groups design */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            Subscription
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your workspace plan, entitlement limits, and billing details
          </p>
        </div>
      </header>

      {/* KPI Overview Grid */}
      <SubscriptionStats status={status} usage={usage?.usage} isLoading={isLoading} />

      {/* Scheduled Cancellation Warning Banner */}
      {status?.cancelAtPeriodEnd && (
        <Card className="border-warning-500/50 bg-warning-50/50 shadow-none dark:bg-warning-950/20">
          <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-warning-500/10 p-2.5 text-warning-600 dark:text-warning-400">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    Subscription Cancellation Scheduled
                  </h3>
                  <Badge variant="warning">Pending Expiry</Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Your plan will remain active until {formatDate(status.currentPeriodEnd!)}. You can restore auto-renewal at any time before this date.
                </p>
              </div>
            </div>
            {canCancel && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => undoCancelMutation.mutate()}
                disabled={undoCancelMutation.isPending}
                className="shrink-0"
              >
                {undoCancelMutation.isPending ? 'Restoring...' : 'Undo Cancellation'}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Locked Resources Alert (if downgrade caused resource lock) */}
      <LockedResourcesAlert
        lockedResources={lockedResources ?? []}
        onUpgradeClick={scrollToPlans}
      />

      {/* Usage & Quota Health Grid */}
      <Card className="border-border bg-card shadow-none">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight text-foreground">
            <Activity className="h-5 w-5 text-primary" />
            Resource Utilization
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            Monitor real-time consumption against your active workspace plan quotas
          </CardDescription>
        </CardHeader>
        <CardContent>
          {usageLoading ? (
            <div className="grid gap-4 md:grid-cols-2">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : usageItems.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {usageItems.map((item) => (
                <UsageMeter
                  key={item.featureKey}
                  label={item.name}
                  current={item.currentUsage}
                  limit={numericLimit(item.limitValue)}
                  formatValue={(value) => formatUsageValue(item.featureKey, value)}
                  limitLabel={formatLimitValue(item.featureKey, item.limitValue)}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No quota utilization data available for this workspace.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Plans Section */}
      <div ref={plansRef}>
        <Card className="border-border bg-card shadow-none">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold tracking-tight text-foreground">
              <CreditCard className="h-5 w-5 text-primary" />
              {status?.subscriptionId ? 'Change Plan' : 'Available Plans'}
            </CardTitle>
            <CardDescription className="text-sm text-muted-foreground">
              {status?.subscriptionId
                ? 'Select an alternative plan to adapt your workspace capacity and features'
                : 'Choose a subscription plan to unlock features for your team'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {plansLoading ? (
              <div className="grid gap-5 md:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-72 w-full rounded-xl" />
                ))}
              </div>
            ) : (
              <div className="grid gap-5 md:grid-cols-3">
                {plans?.map((plan) => (
                  <PlanCard
                    key={plan.code}
                    plan={plan}
                    isCurrentPlan={status?.planCode === plan.code}
                    onSubscribe={
                      can(
                        status?.subscriptionId
                          ? PERMISSION_KEYS.SUBSCRIPTION_CHANGE_PLAN
                          : PERMISSION_KEYS.SUBSCRIPTION_SUBSCRIBE
                      )
                        ? handlePlanSelect
                        : undefined
                    }
                    isLoading={
                      selectedPlanCode === plan.code &&
                      (subscribeMutation.isPending || changePlanMutation.isPending)
                    }
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Plan Confirmation Modal */}
      <PlanConfirmDialog
        plan={selectedPlan}
        currentStatus={status}
        open={!!selectedPlanCode}
        onOpenChange={(open) => !open && setSelectedPlanCode(null)}
        onConfirm={handleConfirmPlan}
        isPending={subscribeMutation.isPending || changePlanMutation.isPending}
      />

      {/* Danger Zone: Cancel Subscription */}
      {status?.subscriptionId && !status.cancelAtPeriodEnd && canCancel && (
        <Card className="border-destructive/30 bg-destructive/5 shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-destructive">
              Cancel Subscription
            </CardTitle>
            <CardDescription className="text-xs text-muted-foreground">
              Your subscription will remain active until the end of the current billing period. Once cancelled, your workspace limits will automatically revert to default quotas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => setCancelDialogOpen(true)}
              disabled={cancelMutation.isPending}
            >
              Cancel Subscription
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Cancel Confirmation Dialog */}
      <CancelConfirmDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        onConfirm={() => cancelMutation.mutate()}
        isPending={cancelMutation.isPending}
        currentPeriodEnd={status?.currentPeriodEnd}
      />
    </div>
  )
}